import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { rtdb } from "../../../config/firebase";
import { ref, push, update } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

export default function BeritaModal({ berita, onClose, isNew }) {
  const [formData, setFormData] = useState({
    judul: berita?.judul || "",
    keterangan: berita?.keterangan || "",
    tanggal: berita?.tanggal || new Date().toISOString().substring(0, 10),
    waktu: berita?.waktu || "08:00",
    fotoUrl: berita?.fotoUrl || "",
    file: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const storage = getStorage(); // ✅ Firebase Storage
  const BUCKET_PATH = "Berita"; // ✅ Folder bucket di Firebase Storage
  const title = isNew ? "Tambah Berita Baru" : `Edit Berita: ${berita?.judul || ""}`;

  useEffect(() => {
    setFormData({
      judul: berita?.judul || "",
      keterangan: berita?.keterangan || "",
      tanggal: berita?.tanggal || new Date().toISOString().substring(0, 10),
      waktu: berita?.waktu || "08:00",
      fotoUrl: berita?.fotoUrl || "",
      file: null,
    });
  }, [berita]);

  // 🔹 Handle input teks
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Handle input file (preview gambar)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.fotoUrl && formData.fotoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.fotoUrl);
      }
      setFormData((prev) => ({
        ...prev,
        file,
        fotoUrl: URL.createObjectURL(file),
      }));
    }
  };

  // 🔹 Handle submit (upload + simpan ke RTDB)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let finalFotoUrl = formData.fotoUrl;

      // ✅ Upload ke Firebase Storage jika ada file baru
      if (formData.file) {
        const file = formData.file;
        const fileName = `berita_${Date.now()}_${file.name}`;
        const filePath = `${BUCKET_PATH}/${fileName}`;

        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, file);
        finalFotoUrl = await getDownloadURL(fileRef);
      }

      // ✅ Data yang disimpan ke Realtime Database
      const dataToSave = {
        judul: formData.judul,
        keterangan: formData.keterangan,
        tanggal: formData.tanggal,
        waktu: formData.waktu,
        fotoUrl: finalFotoUrl,
        timestamp: Date.now(),
      };

      if (isNew) {
        await push(ref(rtdb, "berita"), dataToSave);
        toast.success("📰 Berita berhasil ditambahkan!");
      } else {
        await update(ref(rtdb, `berita/${berita.id}`), dataToSave);
        toast.success("✅ Perubahan berita berhasil disimpan!");
      }

      onClose();
    } catch (err) {
      console.error("❌ Gagal menyimpan berita:", err);
      toast.error(err.message || "Terjadi kesalahan saat menyimpan berita!");
    } finally {
      setIsSubmitting(false);
      if (formData.fotoUrl && formData.fotoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.fotoUrl);
      }
    }
  };

  // 🔹 Render modal ke portal
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex justify-center items-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        {/* HEADER */}
        <div className="flex justify-between items-center p-5 border-b">
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* Kiri */}
          <div className="space-y-4">
            <div className="flex flex-col items-center border border-gray-200 rounded-lg p-3 bg-gray-50">
              {formData.fotoUrl ? (
                <img
                  src={formData.fotoUrl}
                  alt="Pratinjau Foto"
                  className="w-full h-40 object-cover rounded-lg mb-3 shadow"
                />
              ) : (
                <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 mb-3">
                  Belum ada foto
                </div>
              )}

              <label className="cursor-pointer inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-300 rounded-full text-sm font-medium text-blue-700 hover:bg-blue-100 transition">
                <PhotoIcon className="w-4 h-4 mr-2" />
                Pilih Foto
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isSubmitting}
                />
              </label>
              <p className="text-xs text-gray-400 mt-1">
                Pilih gambar dari perangkat (JPG/PNG)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Tanggal
                </label>
                <input
                  type="date"
                  name="tanggal"
                  value={formData.tanggal}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Waktu
                </label>
                <input
                  type="time"
                  name="waktu"
                  value={formData.waktu}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          </div>

          {/* Kanan */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Judul Berita
              </label>
              <input
                type="text"
                name="judul"
                value={formData.judul}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Keterangan (Isi Ringkas)
              </label>
              <textarea
                name="keterangan"
                value={formData.keterangan}
                onChange={handleChange}
                required
                rows="7"
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500 resize-none"
              ></textarea>
            </div>

            <div className="pt-4 flex justify-end border-t">
              <button
                type="button"
                onClick={onClose}
                className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                disabled={isSubmitting}
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-md disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Memproses..."
                  : isNew
                  ? "Publikasikan"
                  : "Simpan Perubahan"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
